import React, { useEffect, useState } from 'react';
import axios from 'axios';
import ReportTemplate from '../../components/reports/ReportTemplate';
import { AMAZON_IMPORTS } from '../../consts/reports';

const ImportCard = ({ title, endpoint, onResult }) => {
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const submit = async (event) => {
    event.preventDefault();
    if (!file) return;
    setLoading(true);
    setError(null);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const { data } = await axios.post(endpoint, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      onResult(title, data);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={submit} className="rounded-lg border border-gray-200 bg-white p-5 shadow-sm">
      <h2 className="mb-4 text-lg font-semibold text-gray-900">{title}</h2>
      <input
        type="file"
        accept=".csv,text/csv"
        onChange={(event) => setFile(event.target.files?.[0] || null)}
        className="mb-4 block w-full text-sm"
      />
      <button
        type="submit"
        disabled={!file || loading}
        className="rounded-md bg-gray-900 px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
      >
        {loading ? 'Importing…' : 'Import CSV'}
      </button>
      {error && <p className="mt-3 text-sm text-red-600">{error}</p>}
    </form>
  );
};

const value = (item) => (item === null || item === undefined ? '—' : String(item));

const AmazonImportsReport = ({ marginFromSidebar }) => {
  const [results, setResults] = useState([]);
  const [backfillStart, setBackfillStart] = useState('2025-01-01');
  const [backfillEnd, setBackfillEnd] = useState(new Date().toISOString().slice(0, 10));
  const [backfill, setBackfill] = useState({ status: 'idle', months: [] });
  const [backfillError, setBackfillError] = useState(null);
  const onResult = (title, result) => {
    setResults((previous) => [{ title, result }, ...previous.filter((item) => item.title !== title)]);
  };

  const startBackfill = async (event) => {
    event.preventDefault();
    setBackfillError(null);
    try {
      const { data } = await axios.post('/api/v1/amazon/imports/transaction-detail/backfill', {
        startDate: backfillStart,
        endDate: backfillEnd,
      });
      setBackfill(data.job);
    } catch (error) {
      setBackfillError(error.response?.data?.error || error.message);
      if (error.response?.data?.job) setBackfill(error.response.data.job);
    }
  };

  useEffect(() => {
    if (backfill.status !== 'running') return undefined;
    const poll = async () => {
      try {
        const { data } = await axios.get('/api/v1/amazon/imports/transaction-detail/backfill/status');
        setBackfill(data.job);
      } catch (error) {
        setBackfillError(error.response?.data?.error || error.message);
      }
    };
    const interval = setInterval(poll, 3000);
    poll();
    return () => clearInterval(interval);
  }, [backfill.status]);

  return (
    <ReportTemplate
      title="Amazon CSV Imports"
      reportId={AMAZON_IMPORTS}
      marginFromSidebar={marginFromSidebar}
      renderChildrenOnly
    >
      <div className="space-y-6">
        <p className="text-sm text-gray-600">
          Upload Amazon Custom Unified Transaction and Ads campaign exports. The newest upload
          covering a date range supersedes earlier rows for that range; older rows remain available
          for audit. Transaction posted dates use Amazon's Pacific calendar date, not the
          America/Denver daily-metrics date; late-evening Pacific transactions can fall on the
          previous Denver day.
        </p>
        <div className="grid gap-5 md:grid-cols-2">
          <ImportCard
            title="Transaction Detail"
            endpoint="/api/v1/amazon/imports/transaction-detail"
            onResult={onResult}
          />
          <ImportCard
            title="Ad Spend"
            endpoint="/api/v1/amazon/imports/ad-spend"
            onResult={onResult}
          />
        </div>
        <section className="rounded-lg border border-gray-200 bg-white p-5 shadow-sm">
          <h2 className="mb-2 text-lg font-semibold text-gray-900">Transaction Detail Backfill</h2>
          <p className="mb-4 text-sm text-gray-600">
            Fetch transaction detail month by month. Failed months are recorded and do not stop the remaining months.
          </p>
          <form onSubmit={startBackfill} className="flex flex-wrap items-end gap-3">
            <label className="text-sm text-gray-700">
              Start date
              <input
                type="date"
                value={backfillStart}
                onChange={(event) => setBackfillStart(event.target.value)}
                className="mt-1 block rounded-md border border-gray-300 px-3 py-2"
                required
              />
            </label>
            <label className="text-sm text-gray-700">
              End date
              <input
                type="date"
                value={backfillEnd}
                onChange={(event) => setBackfillEnd(event.target.value)}
                className="mt-1 block rounded-md border border-gray-300 px-3 py-2"
                required
              />
            </label>
            <button
              type="submit"
              disabled={backfill.status === 'running'}
              className="rounded-md bg-gray-900 px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
            >
              {backfill.status === 'running' ? 'Backfill running…' : 'Start backfill'}
            </button>
          </form>
          {backfillError && <p className="mt-3 text-sm text-red-600">{backfillError}</p>}
          {backfill.status !== 'idle' && (
            <div className="mt-4 text-sm">
              <p className="font-medium text-gray-900">
                Status: {backfill.status}
                {backfill.completedMonths != null && backfill.totalMonths
                  ? ` (${backfill.completedMonths}/${backfill.totalMonths} months)`
                  : ''}
              </p>
              {backfill.error && <p className="mt-1 text-red-600">{backfill.error}</p>}
              {!!backfill.months?.length && (
                <div className="mt-3 max-h-64 overflow-auto rounded border border-gray-200">
                  <table className="min-w-full text-left text-xs">
                    <thead className="bg-gray-50">
                      <tr><th className="px-3 py-2">Month</th><th className="px-3 py-2">Status</th><th className="px-3 py-2">Rows</th><th className="px-3 py-2">Error</th></tr>
                    </thead>
                    <tbody>
                      {backfill.months.map((month) => (
                        <tr key={`${month.start}-${month.end}`} className="border-t border-gray-100">
                          <td className="px-3 py-2">{month.start} – {month.end}</td>
                          <td className="px-3 py-2">{month.status}</td>
                          <td className="px-3 py-2">{month.inserted ?? month.parsed ?? '—'}</td>
                          <td className="px-3 py-2 text-red-600">{month.error || '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </section>
        {results.map(({ title, result }) => (
          <section key={title} className="rounded-lg border border-gray-200 bg-white p-5 shadow-sm">
            <h2 className="mb-3 text-lg font-semibold text-gray-900">{title} result</h2>
            <div className="mb-4 grid grid-cols-2 gap-3 text-sm md:grid-cols-5">
              <div><strong>Parsed:</strong> {result.parsed}</div>
              <div><strong>Inserted:</strong> {result.inserted}</div>
              <div><strong>Skipped:</strong> {result.skipped_duplicates}</div>
              <div><strong>Superseded:</strong> {result.superseded}</div>
              <div><strong>Batch:</strong> {result.batch_id}</div>
            </div>
            <h3 className="mb-2 font-medium text-gray-900">Totals</h3>
            <div className="grid gap-2 text-sm md:grid-cols-4">
              {Object.entries(result.totals || {}).map(([key, item]) => (
                <div key={key}><strong>{key}:</strong> {typeof item === 'object' ? JSON.stringify(item) : value(item)}</div>
              ))}
            </div>
            {!!result.errors?.length && (
              <div className="mt-4">
                <h3 className="mb-2 font-medium text-red-700">Row errors</h3>
                <ul className="list-disc pl-5 text-sm text-red-700">
                  {result.errors.map((item) => <li key={`${item.line}-${item.message}`}>Line {item.line}: {item.message}</li>)}
                </ul>
              </div>
            )}
          </section>
        ))}
      </div>
    </ReportTemplate>
  );
};

export default AmazonImportsReport;
